/* StepTemplate — Wizard Step 2 (renamed: "Catalog" = image grid; "Linesheet" = table). */
import { useEffect } from "react";
import { Check, Grid3x3, Table, ChevronLeft, Sparkles, ExternalLink } from "lucide-react";

const TEMPLATES = [
  {
    id: "catalog",
    label: "Catalog",
    desc: "Image-led grid of product cards. Best for brands with strong product photography. The default for Beauty.",
    icon: Grid3x3,
    badge: "Recommended for Beauty",
    bullets: [
      "Hero image on every card",
      "Badge chips (New / Top / Special)",
      "Fulfillment + MOQ chips",
      "Retail strikethrough + margin %",
    ],
    // The image-grid preview was originally hosted at /linesheet — keep
    // the URL stable, the label inside the page reflects the new name.
    previewUrl: "/preview/catalog/linesheet",
  },
  {
    id: "linesheet",
    label: "Linesheet",
    desc: "Dense table layout. Best for buyers who care more about specs than imagery. Quick-order paste at the top.",
    icon: Table,
    badge: null,
    bullets: [
      "Sortable data table",
      "Bulk paste SKU + qty",
      "Compact rows",
      "Keyboard-first navigation",
    ],
    previewUrl: "/preview/catalog/catalog-sheet",
  },
];

export default function StepTemplate({ value, onChange, onNext, onBack, industryDefault }) {
  useEffect(() => {
    if (!value && industryDefault) onChange(industryDefault);
    // eslint-disable-next-line
  }, [industryDefault]);

  return (
    <div data-testid="setup-step-template">
      <div className="text-[10px] mono uppercase tracking-[0.24em] text-foreground/55">Step 2 of 4 · Template</div>
      <h1 className="display text-3xl mt-2 tracking-tight">Pick a catalog template</h1>
      <p className="text-sm text-foreground/60 mt-2 max-w-2xl">
        This is just how your catalog <em>looks</em> to buyers — the underlying data stays the same. You can switch any time without losing products.
      </p>

      <div className="mt-7 grid lg:grid-cols-2 gap-4">
        {TEMPLATES.map(t => {
          const active = value === t.id;
          return (
            <div key={t.id}
              className={`rounded-2xl border p-5 transition-all ${active ? "border-foreground bg-foreground/[0.03] ring-2 ring-foreground/10" : "border-border bg-white"}`}>
              <button onClick={() => onChange(t.id)} data-testid={`template-${t.id}`} className="w-full text-left">
                <div className="flex items-start justify-between">
                  <span className={`w-12 h-12 rounded-lg grid place-items-center ${active ? "bg-foreground text-background" : "bg-secondary text-foreground/65"}`}>
                    <t.icon className="w-5 h-5"/>
                  </span>
                  <div className="flex items-center gap-2">
                    {t.badge && <span className="chip bg-emerald-50 border border-emerald-200 text-emerald-700 text-[9.5px]"><Sparkles className="w-2.5 h-2.5"/>{t.badge}</span>}
                    {active && <Check className="w-5 h-5 text-foreground"/>}
                  </div>
                </div>
                <div className="display text-xl mt-4">{t.label}</div>
                <div className="text-[12.5px] text-foreground/60 mt-1.5 leading-relaxed">{t.desc}</div>
                <ul className="mt-4 space-y-1.5">
                  {t.bullets.map((b, i) => (
                    <li key={i} className="text-[12px] text-foreground/70 inline-flex items-center gap-1.5">
                      <Check className="w-3 h-3 text-emerald-600 shrink-0"/>{b}
                    </li>
                  ))}
                </ul>
              </button>
              <a href={t.previewUrl} target="_blank" rel="noreferrer" className="mt-4 inline-flex items-center gap-1 text-[11.5px] text-foreground/60 hover:text-foreground underline-offset-2 hover:underline">
                Open full preview <ExternalLink className="w-3 h-3"/>
              </a>
            </div>
          );
        })}
      </div>

      <div className="mt-8 flex items-center justify-between">
        <button onClick={onBack} className="h-10 px-4 rounded-full text-sm text-foreground/65 hover:text-foreground inline-flex items-center gap-1.5"><ChevronLeft className="w-3.5 h-3.5"/>Back</button>
        <button onClick={onNext} disabled={!value} data-testid="template-next" className="h-11 px-5 rounded-full bg-foreground text-background hover:bg-foreground/90 text-sm font-medium disabled:opacity-50">
          Continue →
        </button>
      </div>
    </div>
  );
}
